const API_URL = process.env.REACT_APP_BACKEND_URL;

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

const fetchWithAuth = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}/api/v1${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  return handleResponse(response);
};

// Company
export const signupCompany = async (data) => {
  const response = await fetch(`${API_URL}/api/v1/auth/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const getCompany = (companyId) => fetchWithAuth(`/companies/${companyId}`);
export const updateCompany = (companyId, data) => fetchWithAuth(`/companies/${companyId}`, { method: 'PUT', body: JSON.stringify(data) });

// Dashboard
export const getDashboardOverview = () => fetchWithAuth('/dashboard/overview');
export const getRecentActivity = (limit = 20) => fetchWithAuth(`/dashboard/activity?limit=${limit}`);
export const getExpiringBlocks = (hours = 24) => fetchWithAuth(`/dashboard/expiring-blocks?hours=${hours}`);

// Developers
export const getDevelopers = () => fetchWithAuth('/developers');
export const getDeveloper = (id) => fetchWithAuth(`/developers/${id}`);
export const createDeveloper = (data) => fetchWithAuth('/developers', { method: 'POST', body: JSON.stringify(data) });
export const updateDeveloper = (id, data) => fetchWithAuth(`/developers/${id}`, { method: 'PUT', body: JSON.stringify(data) });

// Projects
export const getProjects = () => fetchWithAuth('/projects');
export const getProject = (id) => fetchWithAuth(`/projects/${id}`);
export const createProject = (data) => fetchWithAuth('/projects', { method: 'POST', body: JSON.stringify(data) });
export const updateProject = (id, data) => fetchWithAuth(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteProject = (id) => fetchWithAuth(`/projects/${id}`, { method: 'DELETE' });

// Towers
export const getTowers = (projectId = null) => {
  const query = projectId ? `?project_id=${projectId}` : '';
  return fetchWithAuth(`/towers${query}`);
};
export const createTower = (data) => fetchWithAuth('/towers', { method: 'POST', body: JSON.stringify(data) });
export const updateTower = (id, data) => fetchWithAuth(`/towers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTower = (id) => fetchWithAuth(`/towers/${id}`, { method: 'DELETE' });

// Units
export const getUnits = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.project_id) params.append('project_id', filters.project_id);
  if (filters.tower_id) params.append('tower_id', filters.tower_id);
  if (filters.status) params.append('status', filters.status);
  if (filters.occupancy_status) params.append('occupancy_status', filters.occupancy_status);
  if (filters.channel_id) params.append('channel_id', filters.channel_id);
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchWithAuth(`/units${query}`);
};
export const getUnit = (id) => fetchWithAuth(`/units/${id}`);
export const createUnit = (data) => fetchWithAuth('/units', { method: 'POST', body: JSON.stringify(data) });
export const createUnitsBulk = (units) => fetchWithAuth('/units/bulk', { method: 'POST', body: JSON.stringify(units) });

// CSV Upload
export const uploadUnitsCSV = async (file, projectId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  
  let url = `${API_URL}/api/v1/units/upload-csv`;
  if (projectId) {
    url += `?project_id=${projectId}`;
  }
  
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  return handleResponse(response);
};

export const downloadCSVTemplate = () => {
  window.open(`${API_URL}/api/v1/units/csv-template`, '_blank');
};

export const updateUnit = (id, data) => fetchWithAuth(`/units/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const changeUnitStatus = (id, statusData, channelId = null) => {
  const query = channelId ? `?channel_id=${channelId}` : '';
  return fetchWithAuth(`/units/${id}/status${query}`, { method: 'POST', body: JSON.stringify(statusData) });
};
export const deleteUnit = (id) => fetchWithAuth(`/units/${id}`, { method: 'DELETE' });

// Bulk Operations
export const bulkChangeStatus = (data) => fetchWithAuth('/units/bulk-status', { method: 'POST', body: JSON.stringify(data) });
export const bulkChangeOccupancy = (data) => fetchWithAuth('/units/bulk-occupancy', { method: 'POST', body: JSON.stringify(data) });

// Channels
export const getChannels = () => fetchWithAuth('/channels');
export const getChannel = (id) => fetchWithAuth(`/channels/${id}`);
export const createChannel = (data) => fetchWithAuth('/channels', { method: 'POST', body: JSON.stringify(data) });
export const updateChannel = (id, data) => fetchWithAuth(`/channels/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteChannel = (id) => fetchWithAuth(`/channels/${id}`, { method: 'DELETE' });
export const allocateUnitsToChannel = (channelId, unitIds) => {
  return fetchWithAuth(`/channels/${channelId}/allocate`, { 
    method: 'POST', 
    body: JSON.stringify({ channel_id: channelId, unit_ids: unitIds }) 
  });
};
export const getChannelUnits = (channelId) => fetchWithAuth(`/channels/${channelId}/units`);

// Users
export const getUsers = () => fetchWithAuth('/users');
export const createUser = (data) => fetchWithAuth('/users', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (id, data) => fetchWithAuth(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteUser = (id) => fetchWithAuth(`/users/${id}`, { method: 'DELETE' });

// Audit Logs
export const getAuditLogs = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.entity_type) params.append('entity_type', filters.entity_type);
  if (filters.entity_id) params.append('entity_id', filters.entity_id);
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.skip) params.append('skip', filters.skip);
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchWithAuth(`/audit-logs${query}`);
};

// CRM (Mock)
export const getCRMLeads = (unitId = null) => {
  const query = unitId ? `?unit_id=${unitId}` : '';
  return fetchWithAuth(`/crm/leads${query}`);
};
export const createCRMLead = (data) => fetchWithAuth('/crm/leads', { method: 'POST', body: JSON.stringify(data) });
export const updateCRMLead = (id, data) => fetchWithAuth(`/crm/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) });

// Tasks
export const expireSoftBlocks = () => fetchWithAuth('/tasks/expire-blocks', { method: 'POST' });

// Subscriptions & Plans
export const getPublicPlans = async () => {
  const response = await fetch(`${API_URL}/api/v1/public/plans`);
  return handleResponse(response);
};
export const getPublicAddons = async () => {
  const response = await fetch(`${API_URL}/api/v1/public/add-ons`);
  return handleResponse(response);
};
export const getSubscriptionPlans = () => fetchWithAuth('/subscriptions/plans');
export const getCurrentSubscription = () => fetchWithAuth('/subscriptions/current');
export const getUsage = () => fetchWithAuth('/subscriptions/usage');
export const subscribeToPlan = (data) => fetchWithAuth('/subscriptions/subscribe', { method: 'POST', body: JSON.stringify(data) });
export const verifyPayment = (data) => fetchWithAuth('/subscriptions/verify-payment', { method: 'POST', body: JSON.stringify(data) });
export const validateDiscount = (code, planId) => fetchWithAuth('/subscriptions/validate-discount', { method: 'POST', body: JSON.stringify({ code, plan_id: planId }) });
export const getDiscounts = () => fetchWithAuth('/subscriptions/discounts');
export const createDiscount = (data) => fetchWithAuth('/subscriptions/discounts', { method: 'POST', body: JSON.stringify(data) });

export default {
  signupCompany,
  getCompany,
  updateCompany,
  getDashboardOverview,
  getRecentActivity,
  getExpiringBlocks,
  getDevelopers,
  getDeveloper,
  createDeveloper,
  updateDeveloper,
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  getTowers,
  createTower,
  updateTower,
  deleteTower,
  getUnits,
  getUnit,
  createUnit,
  createUnitsBulk,
  uploadUnitsCSV,
  downloadCSVTemplate,
  updateUnit,
  changeUnitStatus,
  deleteUnit,
  bulkChangeStatus,
  bulkChangeOccupancy,
  getChannels,
  getChannel,
  createChannel,
  updateChannel,
  deleteChannel,
  allocateUnitsToChannel,
  getChannelUnits,
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getAuditLogs,
  getCRMLeads,
  createCRMLead,
  updateCRMLead,
  expireSoftBlocks,
  getPublicPlans,
  getPublicAddons,
  getSubscriptionPlans,
  getCurrentSubscription,
  getUsage,
  subscribeToPlan,
  verifyPayment,
  validateDiscount,
  getDiscounts,
  createDiscount,
};
